import React, { useEffect, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { ThemeContext, THEME_KEY, BRAND_COLOR_KEY } from "@/shared/lib/theme";
import type { ThemeContextShape } from "@/shared/lib/theme";
import { useSyncThirdPartyLocale } from "@/shared/hooks/useSyncThirdPartyLocale";
import { detectHost } from "@/auth";

/**
 * Any recognised host — an iframe OR a native WebView. The theme rules below
 * all turn on "did someone else launch this page and own its appearance",
 * which was written as `isEmbedded()` when the iframe was the only host.
 */
const isHosted = () => detectHost() !== "none";

type ThemeType = "light" | "dark";

// One-time migration: the design system now has a fixed brand color, so any
// user-set custom color is no longer honored. Clear the legacy key once on
// first mount so localStorage stays clean.
function migrateLegacyBrandColor() {
  try {
    if (localStorage.getItem(BRAND_COLOR_KEY) !== null) {
      localStorage.removeItem(BRAND_COLOR_KEY);
      // eslint-disable-next-line no-console
      console.info(
        "[design-system] Cleared legacy BRAND_COLOR_KEY from localStorage",
      );
    }
  } catch {
    // ignore — localStorage may be unavailable in some embedded contexts
  }
}

/**
 * The host's boot-time theme, read off the URL (`?theme=light|dark`).
 *
 * Any recognised host, not just an iframe: a WebView builds this URL too,
 * and it has no other way to say which theme its app is in — studio:context
 * is a window message and never reaches it (mobile team, 2026-08-25). With
 * no host the param is meaningless (nobody put it there) and this origin's
 * own stored preference stays authoritative.
 */
function bootThemeFromHost(): ThemeType | null {
  if (!isHosted()) return null;
  const value = new URLSearchParams(window.location.search).get("theme");
  return value === "dark" || value === "light" ? value : null;
}

export default function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    migrateLegacyBrandColor();
  }, []);

  const [theme, setTheme] = useState<ThemeType>(() => {
    // Embedded, the HOST owns the theme (adopted from studio:context via
    // HostContextBridge). Never trust this origin's stored value at boot —
    // a dark leftover from a standalone/harness session paints the first
    // frames black before the host's context arrives on iframe load.
    //
    // The host's own value arrives twice, and the difference matters:
    //   · `?theme=` on the frame URL — a BOOT HINT, frozen at mount. The host
    //     cannot keep it current (rewriting `src` reloads the Studio and
    //     destroys the editor session), so it is only ever right for the
    //     first paint — which is exactly the white-flash window a dark-mode
    //     vendor sees while studio:context is in flight.
    //   · `studio:context` — the live value, and always the higher authority.
    // Absent or junk falls back to "light", so an older host that sends no
    // param behaves exactly as it did before this existed.
    if (isHosted()) return bootThemeFromHost() ?? "light";
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored === "dark") return "dark";
      return "light";
    } catch {
      return "light";
    }
  });

  // Apply theme class to document root for the `.dark` selector in
  // variables.scss to take effect.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    // Hosted, the adopted theme is the host's, not this origin's — writing
    // it here is what plants the stale value the boot guard above ignores.
    if (isHosted()) return;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const algorithm =
    theme === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  // Font stack shared with Tailwind / global SCSS — Open Sans first, native
  // OS fallback chain after.
  const fontFamily =
    '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // AntD needs LITERAL color values for its tokens — it parses them through
  // its color algorithm to derive related tokens (elevated bgs, hover states,
  // borders on popups, etc). Passing "var(--…)" strings breaks that derivation
  // and AntD silently falls back to its dark-mode defaults, which is what
  // caused dropdowns / popovers to render with black surfaces in light mode.
  // Mirror the values from variables.scss so visual parity is preserved; the
  // CSS variables remain the source of truth for non-AntD consumers.
  const isDark = theme === "dark";
  const token = {
    colorPrimary: isDark ? "#3ECF92" : "#22b17a",
    colorText: isDark ? "#F4F4F5" : "#333333",
    colorTextSecondary: isDark ? "#A1A1AA" : "#6F6F6F",
    colorTextTertiary: isDark ? "#71717A" : "#9CA3AF",
    colorBgBase: isDark ? "#0A0A0A" : "#ffffff",
    colorBgContainer: isDark ? "#1F1F1F" : "#ffffff",
    colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
    colorBgLayout: isDark ? "#141414" : "#FAFAFA",
    // Reverted to pre-design-system border colors — the spec's #F5F5F5 /
    // #EBEBEB were too mild on white surfaces; AntD's borders disappeared.
    colorBorder: isDark ? "#525252" : "#e5e7eb",
    colorBorderSecondary: isDark ? "#707070" : "#d1d5db",
    borderRadius: 10,
    borderRadiusSM: 4,
    borderRadiusLG: 16,
    fontFamily,
  };

  // Form-control radius — every text/select/picker family pinned to 8px
  // across all three sizes (small/middle/large) so dense forms read as
  // restrained and clinical rather than the default AntD pillow corners
  // (large = 16px). Buttons/Cards/Dropdowns intentionally keep the broader
  // token-driven radii. Mirrors --radius-input from variables.scss.
  const INPUT_RADII = {
    borderRadius: 8,
    borderRadiusLG: 8,
    borderRadiusSM: 8,
  };

  // Form-control heights — pinned across the family so an inline row of
  // <Select> + <Input> + <DatePicker> + <Button> all render at the same
  // pixel height for whatever `size` prop is in use. AntD's defaults are
  // inconsistent across components (Button = 40 at middle, Select = 32
  // at middle), which produced visible mismatches whenever two form
  // controls sat side-by-side at the same `size` prop. Applying the
  // same height triplet to every form-control family fixes that.
  const INPUT_SIZES = {
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
  };

  const components = {
    Button: { ...INPUT_SIZES },
    Input: { ...INPUT_SIZES, ...INPUT_RADII },
    InputNumber: { ...INPUT_SIZES, ...INPUT_RADII },
    Mentions: { ...INPUT_SIZES, ...INPUT_RADII },
    AutoComplete: { ...INPUT_SIZES, ...INPUT_RADII },
    Cascader: { ...INPUT_SIZES, ...INPUT_RADII },
    DatePicker: { ...INPUT_SIZES, ...INPUT_RADII },
    TimePicker: { ...INPUT_SIZES, ...INPUT_RADII },
    Card: { paddingLG: 24 },
    // Dropdown / Popover / Tooltip / Select popups share colorBgElevated, but
    // we set per-component overrides as well so any future AntD upgrade that
    // changes the derivation rule still lands on the right surface.
    Dropdown: {
      colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
      colorText: isDark ? "#F4F4F5" : "#333333",
    },
    Select: {
      colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
      colorText: isDark ? "#F4F4F5" : "#333333",
      ...INPUT_SIZES,
      ...INPUT_RADII,
    },
    Popover: {
      colorBgElevated: isDark ? "#1F1F1F" : "#ffffff",
      colorText: isDark ? "#F4F4F5" : "#333333",
    },
    Tooltip: {
      colorBgSpotlight: isDark ? "#1F1F1F" : "#333333",
      colorTextLightSolid: "#ffffff",
    },
  };

  const { antdLocale } = useSyncThirdPartyLocale();

  const value: ThemeContextShape = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        locale={antdLocale}
        theme={{
          algorithm,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          token: token as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          components: components as any,
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
